import { useCCFeatureFlags } from '@hooks/useFeatureFlags';
import { createContext, type ReactNode, useCallback, useContext, useMemo } from 'react';

type FeatureFlagsResponse = {
	results?: Record<string, { enabled: boolean }>;
};

interface FeatureContextType {
	featureFlags: Record<string, { enabled: boolean }>;
	isLoading: boolean;
	// TODO remove with FF
	isFetched: boolean;
	error: Error | null;
	hasFeature: (feature: string) => boolean;
}

const FeatureContext = createContext<FeatureContextType | null>(null);

export const CCFeatureFlagContextProvider = ({ children }: { children: ReactNode }) => {
	const {
		data: featureFlagsData,
		isLoading: isFeatureFlagsLoading,
		isFetched: areFeatureFlagsFetched,
		error: featureFlagsError,
	} = useCCFeatureFlags();

	const featureFlags = (featureFlagsData as FeatureFlagsResponse)?.results ?? {};

	const hasFeature = useCallback(
		(feature: string) => Boolean(featureFlags[feature]?.enabled),
		[featureFlags],
	);

	const value = useMemo(
		() => ({
			featureFlags,
			isLoading: isFeatureFlagsLoading,
			isFetched: areFeatureFlagsFetched,
			error: featureFlagsError ?? null,
			hasFeature,
		}),
		[
			featureFlags,
			isFeatureFlagsLoading,
			areFeatureFlagsFetched,
			featureFlagsError,
			hasFeature,
		],
	);

	return <FeatureContext.Provider value={value}>{children}</FeatureContext.Provider>;
};

export const useCCFeatureFlagsContext = () => {
	const context = useContext(FeatureContext);

	if (!context) {
		throw new Error(
			'useCCFeatureFlagsContext must be used within a CCFeatureFlagContextProvider',
		);
	}

	return context;
};
